import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { SEARCH_PAGE_CATEGORIES } from '@backrs_ui/grow-ui/src/pages/Search/search.types';
import { fetchNuggetsUserHasSearched } from '@backrs_ui/grow-ui/src/pages/Search/search.util';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setSearchTermForCategory } from '@backrs_ui/grow-ui/src/redux/search/search';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, Button, SxProps, TextField, Theme, Typography } from '@mui/material';

const BACKRS_APP_URL = process.env.REACT_APP_BACKRS_URL;
const HEADER_STRINGS = appStrings.modules.home.header;

export const Header = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [localSearchTerm, setLocalSearchTerm] = useState('');

  const user = useAppSelector(selectUser);

  const handleSearchEnterKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      dispatch(
        setSearchTermForCategory({
          category: SEARCH_PAGE_CATEGORIES.DEFAULT,
          searchTerm: localSearchTerm,
        }),
      );
      fetchNuggetsUserHasSearched(dispatch, localSearchTerm, 1);
      navigate(APP_ROUTE.SEARCH);
      setLocalSearchTerm('');
    }
  };

  return (
    <Box sx={headerSx}>
      <Typography variant="display-md-bold">{HEADER_STRINGS.welcome(user?.first_name)}</Typography>
      <Box sx={buttonSearchContainerSx}>
        <Box sx={buttonContainerSx}>
          <Button
            hierarchy="Secondary gray"
            size="md"
            icon={user?.catch_up_count && user?.catch_up_count > 0 ? 'Leading' : undefined}
            sx={buttonSx}
            onClick={() => window.open(`${BACKRS_APP_URL}/catchup`, '_blank')}
            startIcon={
              user?.catch_up_count ? (
                <AppIcon
                  iconName="Circle"
                  fill="Colors/Foreground/fg-success-primary"
                  color="Colors/Foreground/fg-success-primary"
                  diameter="8px"
                />
              ) : undefined
            }
          >
            {HEADER_STRINGS.catchUp}
          </Button>
          <Button
            hierarchy="Secondary gray"
            size="md"
            icon="Default"
            sx={buttonSx}
            onClick={() => window.open(`${BACKRS_APP_URL}/tabs/feed`, '_blank')}
          >
            {HEADER_STRINGS.networks}
          </Button>
        </Box>
        <TextField
          variant="outlined"
          placeholder={HEADER_STRINGS.searchBarPlaceholder}
          sx={searchBarSx}
          onKeyDown={handleSearchEnterKey}
          value={localSearchTerm}
          onChange={(e) => setLocalSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <Box onClick={() => navigate(APP_ROUTE.SEARCH)} sx={searchIconContainerSx}>
                <AppIcon
                  iconName="SearchMd"
                  diameter="20px"
                  color="Colors/Background/bg-primary-solid"
                />
              </Box>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: {
    xs: backrs.spacing['spacing-2xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
  paddingY: backrs.spacing['spacing-xl'],
  display: 'flex',
  flexWrap: 'wrap',
  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  justifyContent: 'space-between',
  alignItems: {
    xs: 'flex-start',
    md: 'center',
  },
  gap: backrs.spacing['spacing-xl'],
});

const buttonSearchContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
  width: {
    xs: '100%',
    md: '50%',
  },
});

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  boxSizing: 'border-box',
});

const searchBarSx: SxProps<Theme> = {
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: '100%',
  },
  width: { sm: '60%' },
  maxWidth: { lg: '300px' },
};

const searchIconContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
};

const buttonSx: SxProps<Theme> = {
  minWidth: 'fit-content',
  alignSelf: {
    xs: 'flex-start',
    sm: 'flex-end',
    md: 'flex-start',
  },
};
